/**
 * Model class for Beneficials
 */

import Api from '@/providers/Api'
import _ from 'lodash'
import {store} from '@/store'
import i18n from '@/providers/i18n'
import {required} from "vuelidate/lib/validators"
import Pest from '@/models/Pest'

// build select options based on master list of beneficial types
function buildBeneficialTypeOptions () {
    const beneficialTypes = store.state.beneficialTypes
    let options = _.map(beneficialTypes, (beneficialType) => {
        return {
            value: `beneficial_types/${beneficialType.id}`,
            text: i18n.getBeneficialTypeName(beneficialType.id),
        }
    })
    options = _.sortBy(options, ['text'])
    return options
}

export default {

    // load master list of beneficials into the store
    async loadBeneficials() {
        store.state.beneficials = await Api.getAllItems(null, 'beneficials')
    },

    // load master list of beneficial types into the store
    async loadBeneficialTypes() {
        store.state.beneficialTypes = await Api.getAllItems(null, 'beneficial_types')
    },

    buildBeneficialTypeOptions: buildBeneficialTypeOptions,

    // build checkbox options based on master list
    buildBeneficialOptions() {
        const beneficials = store.state.beneficials
        let options = _.map(beneficials, (beneficial) => {
            return {
                value: `beneficials/${beneficial.id}`, text: i18n.getBeneficialName(beneficial.id)
            }
        })
        return _.sortBy(options, ['text'])
    },

    // build grouped checkbox options based on master list, grouped by beneficial type.
    // beneficials without a type land in a trailing 'Uncategorized' group.
    // values remain as `beneficials/{id}` so no storage format change is required.
    buildBeneficialOptionsNested() {
        const beneficials = store.state.beneficials
        const types = store.state.beneficialTypes

        const typeIdOf = (b) => {
            const raw = b.beneficialTypeId
            if (!raw) return null
            return raw.indexOf('/') !== -1 ? _.last(raw.split('/')) : raw
        }

        const groups = _.map(types, (type) => {
            const belonging = _.filter(beneficials, (b) => typeIdOf(b) === type.id)
            return {
                groupName: i18n.getBeneficialTypeName(type.id),
                typeId: type.id,
                options: _.sortBy(
                    _.map(belonging, (b) => ({
                        value: `beneficials/${b.id}`,
                        text: i18n.getBeneficialName(b.id),
                    })),
                    ['text']
                ),
            }
        })

        const nonEmpty = _.sortBy(
            _.filter(groups, g => g.options.length > 0),
            g => g.groupName.toLowerCase()
        )

        const uncategorized = _.filter(beneficials, (b) => !typeIdOf(b))
        if (uncategorized.length) {
            nonEmpty.push({
                groupName: 'Uncategorized',
                typeId: '__uncategorized__',
                options: _.sortBy(
                    _.map(uncategorized, (b) => ({
                        value: `beneficials/${b.id}`,
                        text: i18n.getBeneficialName(b.id),
                    })),
                    ['text']
                ),
            })
        }
        return nonEmpty
    },

    fields: {
        name: {
            translationKey: 'BENEFICIAL_NAME_FIELD',
            type: 'text',
            validations: { required }
        },
        name_es: {
            translationKey: 'BENEFICIAL_NAME_ES_FIELD',
            type: 'text',
            // validations: { required }
        },
        scientificName: {
            translationKey: 'BENEFICIAL_SCIENTIFIC_NAME_FIELD',
            type: 'text',
            // validations: { required }
        },
        beneficialTypeId: {
            translationKey: 'BENEFICIAL_TYPE_FIELD',
            type: 'select',
            options: [],
            loadOptions: async function(model) {
                this.options = buildBeneficialTypeOptions()
            },
        },
        mainImage: {
            translationKey: 'BENEFICIAL_MAIN_IMAGE_FIELD',
            type: 'image',
        },
        mainImageCitation: {
            translationKey: 'BENEFICIAL_MAIN_IMAGE_CITATION_FIELD',
            type: 'text',
        },
        mainImageCitation_es: {
            translationKey: 'BENEFICIAL_MAIN_IMAGE_CITATION_ES_FIELD',
            type: 'text',
        },
        mainImageAlt: {
            translationKey: 'BENEFICIAL_MAIN_IMAGE_ALT_FIELD',
            type: 'text',
        },
        mainImageAlt_es: {
            translationKey: 'BENEFICIAL_MAIN_IMAGE_ALT_ES_FIELD',
            type: 'text',
        },
        factSheetUrl: {
            translationKey: 'BENEFICIAL_FACT_SHEET_URL_FIELD',
            type: 'url',
        },
        factSheetUrl_es: {
            translationKey: 'BENEFICIAL_FACT_SHEET_URL_ES_FIELD',
            type: 'url',
        },
        pesticides: {
            // TODO
        },
        pests: {
            translationKey: 'BENEFICIAL_PESTS_FIELD',
            type: 'checksgroups',
            options: [],
            loadOptions: async function(model) {
                this.options = Pest.buildPestOptionsNested(this)
            },
            loadOptionsUnnested: async function(model) {
                this.options = Pest.buildPestOptionsUnnested(this)
            },
        },
        textSections: {
            translationKey: 'BENEFICIAL_TEXT_SECTIONS_FIELD',
            type: 'textsections',
        },
    }
}
